import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoEscorpio = () => {
  const articleSchema = createArticleSchema(
    "Escorpio: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Escorpio. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de octubre y el 21 de noviembre.",
    `${window.location.origin}/horoscopo-escorpio`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Escorpio",
        "item": `${window.location.origin}/horoscopo-escorpio`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Escorpio" }
  ];

  return (
    <>
      <Helmet>
        <title>Escorpio: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Escorpio. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de octubre y el 21 de noviembre." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-escorpio`} />
        <meta property="og:title" content="Escorpio: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Escorpio. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-escorpio`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♏</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Escorpio
              </h1>
              <p className="text-xl text-muted-foreground">23 de Octubre - 21 de Noviembre</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Escorpio: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="escorpio" signName="Escorpio" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Escorpio: El Transformador</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Escorpio</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">💧 Elemento: Agua</h4>
                    <p className="text-muted-foreground">Enfoque en la intensidad, el misterio y la regeneración. Escorpio navega las profundidades emocionales.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🦂 Modalidad: Fijo</h4>
                    <p className="text-muted-foreground">Define la profundidad, la obsesión y la lealtad total. Inquebrantable en sus compromisos.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♇♂ Planetas Regentes</h4>
                    <p className="text-muted-foreground">Plutón (Transformación) y Marte (Pasión Oculta). Poder regenerativo y voluntad férrea.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Escorpio: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Intensidad:</strong> Profundidad emocional sin igual</li>
                      <li>• <strong>Poder:</strong> Capacidad transformadora inmensa</li>
                      <li>• <strong>Lealtad Extrema:</strong> Devoción inquebrantable</li>
                      <li>• <strong>Intuición:</strong> Percepción psíquica penetrante</li>
                      <li>• <strong>Resiliencia:</strong> Renace de cualquier crisis</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Celos:</strong> Posesividad extrema</li>
                      <li>• <strong>Venganza:</strong> Dificultad para perdonar</li>
                      <li>• <strong>Secretismo:</strong> Oculta sus verdaderos sentimientos</li>
                      <li>• <strong>Control:</strong> Necesidad de dominar situaciones</li>
                      <li>• <strong>Destructividad:</strong> Puede autosabotearse</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Deseo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Escorpio en el Amor: Pasión, Obsesión y Lazos Inquebrantables</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para Escorpio, el amor es todo o nada. Busca fusión total con su pareja, una unión que trascienda lo físico y llegue al alma. Su amor es intenso, apasionado, leal y a veces obsesivo. No conoce término medio en el amor: o se entrega completamente o no entra en la relación.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una pareja que pueda manejar su intensidad, que valore la profundidad emocional y esté dispuesta a explorar los aspectos más oscuros de la experiencia humana. Los celos y la posesividad son desafíos que debe trabajar. Cuando confía, es el compañero más leal y transformador.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Escorpio en el Trabajo y Finanzas: Investigación y Recursos Compartidos</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Escorpio destaca en investigación, psicología, cirugía, detectives, finanzas, gestión de crisis, terapia profunda y cualquier campo que requiera ir más allá de la superficie. Su capacidad para manejar situaciones intensas y transformar crisis es excepcional.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Escorpio tiene un talento natural para gestionar recursos compartidos, herencias e inversiones. Excelente estratega financiero con visión a largo plazo. Puede acumular riqueza significativa a través de su determinación y habilidad para transformar situaciones.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Escorpio con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♋ Cáncer y ♓ Piscis</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua comparten profundidad emocional e intuición. Con Cáncer hay seguridad emocional mutua y devoción familiar. Con Piscis, una conexión espiritual trascendente y comprensión sin palabras.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♍ Virgo</h4>
                    <p className="text-muted-foreground">
                      Virgo ofrece la estructura práctica que Escorpio necesita para manifestar su poder transformador. Ambos valoran la profundidad, la lealtad y la excelencia en todo lo que hacen.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Escorpio</h3>
                <p className="text-muted-foreground mb-4">
                  La confianza y el perdón son los grandes desafíos de Escorpio. Tauro, su signo opuesto, le enseña sobre simplicidad, disfrute sensorial sin dramatismo y el valor de la paz sobre el control. Los signos de fuego (Aries, Leo, Sagitario) desafían su necesidad de profundidad con ligereza.
                </p>
                <p className="text-muted-foreground">
                  Los signos de aire (Géminis, Libra, Acuario) pueden frustrar a Escorpio con su aparente superficialidad, pero le enseñan sobre desapego emocional y la importancia de la objetividad sobre la intensidad.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoEscorpio;
